import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';

// ──────────────────────────────────────────────────────────────────────────
// LandingPage — public marketing front door with an animated market backdrop
// and an integrated login panel. Shown to unauthenticated visitors; on
// successful login the App swaps to the gated dashboard.
// ──────────────────────────────────────────────────────────────────────────

const ORANGE = '#ff8c00';
const GREEN = '#22c55e';
const RED = '#ef4444';

// Animated candlestick + line chart on a canvas — purely decorative.
const MarketCanvas: React.FC = () => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let w = 0, h = 0;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);

    // Generate a smooth random-walk price series; scroll it leftward.
    const N = 160;
    let prices: number[] = [];
    let seed = 100;
    for (let i = 0; i < N; i++) { seed += (Math.random() - 0.48) * 4; prices.push(seed); }

    let offset = 0;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      offset += 0.4;
      if (offset >= 1) {
        offset = 0;
        prices.shift();
        const last = prices[prices.length - 1];
        prices.push(last + (Math.random() - 0.48) * 4);
      }

      const min = Math.min(...prices), max = Math.max(...prices);
      const range = (max - min) || 1;
      const stepX = w / (N - 2);
      const yOf = (p: number) => h - ((p - min) / range) * (h * 0.7) - h * 0.15;

      // Area gradient under the line
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, 'rgba(255,140,0,0.18)');
      grad.addColorStop(1, 'rgba(255,140,0,0)');
      ctx.beginPath();
      ctx.moveTo(-offset * stepX, yOf(prices[0]));
      prices.forEach((p, i) => ctx.lineTo(i * stepX - offset * stepX, yOf(p)));
      ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
      ctx.fillStyle = grad; ctx.fill();

      // The line itself
      ctx.beginPath();
      ctx.moveTo(-offset * stepX, yOf(prices[0]));
      prices.forEach((p, i) => ctx.lineTo(i * stepX - offset * stepX, yOf(p)));
      ctx.strokeStyle = ORANGE; ctx.lineWidth = 2 * window.devicePixelRatio;
      ctx.stroke();

      // Faint candlesticks behind
      ctx.globalAlpha = 0.25;
      for (let i = 0; i < N; i += 4) {
        const p = prices[i]; if (p == null) continue;
        const x = i * stepX - offset * stepX;
        const up = i > 0 && prices[i] >= prices[i - 4];
        ctx.fillStyle = up ? GREEN : RED;
        const bodyH = 6 * window.devicePixelRatio + Math.random() * 10 * window.devicePixelRatio;
        ctx.fillRect(x - 2 * window.devicePixelRatio, yOf(p) - bodyH / 2, 4 * window.devicePixelRatio, bodyH);
      }
      ctx.globalAlpha = 1;

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />;
};

// Floating ticker chips drifting upward
const FloatingTickers: React.FC = () => {
  const tickers = [
    { s: 'NVDA', c: GREEN }, { s: 'ASML', c: GREEN }, { s: 'AAPL', c: RED },
    { s: 'GLXY', c: GREEN }, { s: 'RHM.DE', c: GREEN }, { s: 'MSFT', c: GREEN },
    { s: 'TSLA', c: RED }, { s: 'GOOG', c: GREEN }, { s: 'AMZN', c: RED },
  ];
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {tickers.map((t, i) => (
        <Box key={t.s} sx={{
          position: 'absolute',
          left: `${(i * 11 + 4) % 92}%`,
          bottom: -40,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: 12, fontWeight: 700, color: t.c, opacity: 0.5,
          animation: `floatUp ${14 + (i % 5) * 3}s linear ${i * 1.6}s infinite`,
          '@keyframes floatUp': {
            '0%': { transform: 'translateY(0)', opacity: 0 },
            '10%': { opacity: 0.5 },
            '90%': { opacity: 0.5 },
            '100%': { transform: 'translateY(-105vh)', opacity: 0 },
          },
        }}>
          {t.s} {t.c === GREEN ? '▲' : '▼'}
        </Box>
      ))}
    </Box>
  );
};

const LandingPage: React.FC = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const err = await login(username.trim(), password);
    setBusy(false);
    if (err) setError(err);
    // On success the AuthProvider state flips and App re-renders into the app.
  };

  return (
    <Box sx={{
      minHeight: '100vh', width: '100%', position: 'relative', overflow: 'hidden',
      background: 'radial-gradient(ellipse at 30% 20%, #0f1a2e 0%, #060912 60%, #03060d 100%)',
      display: 'flex', alignItems: 'center',
    }}>
      <MarketCanvas />
      <FloatingTickers />

      {/* Content grid */}
      <Box sx={{
        position: 'relative', zIndex: 2, width: '100%', maxWidth: 1180, mx: 'auto', px: { xs: 3, md: 6 },
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 6, alignItems: 'center',
      }}>
        {/* Hero copy */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <TrendingUpIcon sx={{ color: ORANGE, fontSize: 38 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: '0.04em', color: '#fff' }}>
              FACTORY<span style={{ color: ORANGE }}>FUNDS</span>
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: { xs: 38, md: 54 }, fontWeight: 800, lineHeight: 1.05, color: '#fff', mb: 2.5,
            letterSpacing: '-0.02em',
          }}>
            Institutional-grade<br />
            <span style={{ color: ORANGE }}>market intelligence.</span>
          </Typography>
          <Typography sx={{ fontSize: 17, color: '#94a3b8', mb: 4, maxWidth: 480, lineHeight: 1.6 }}>
            Live pricing, EDGAR-grade fundamentals, interactive DCF valuation, portfolio
            analytics and real-time signal alerts — in one terminal.
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              ['6,000+', 'stocks with official fundamentals'],
              ['Real-time', 'price streaming + alerts'],
              ['CFA-grade', 'valuation & risk analytics'],
            ].map(([big, small]) => (
              <Box key={small}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: ORANGE, fontFamily: '"IBM Plex Mono", monospace' }}>{big}</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', maxWidth: 140 }}>{small}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Login panel */}
        <Box component="form" onSubmit={submit} sx={{
          bgcolor: 'rgba(10,14,23,0.72)', backdropFilter: 'blur(14px)',
          border: '1px solid rgba(148,163,184,0.18)', borderRadius: 3, p: 4,
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LockIcon sx={{ color: ORANGE, fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>Member access</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#64748b', mb: 3 }}>
            Registered users only. Sign in to continue.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>{error}</Alert>}

          <TextField
            placeholder="Username" value={username} onChange={e => setUsername(e.target.value)}
            fullWidth size="small" autoFocus autoComplete="username"
            sx={{ mb: 2, '& input': { color: '#e2e8f0' },
              '& input::placeholder': { color: '#64748b', opacity: 1 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.25)' } }}
          />
          <TextField
            placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)}
            fullWidth size="small" autoComplete="current-password"
            sx={{ mb: 3, '& input': { color: '#e2e8f0' },
              '& input::placeholder': { color: '#64748b', opacity: 1 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.25)' } }}
          />
          <Button
            type="submit" fullWidth variant="contained" disabled={busy || !username || !password}
            sx={{
              bgcolor: ORANGE, color: '#000', fontWeight: 700, py: 1.2, fontSize: 15,
              '&:hover': { bgcolor: '#ffa733' }, '&.Mui-disabled': { bgcolor: 'rgba(255,140,0,0.3)', color: '#000' },
            }}
          >
            {busy ? <CircularProgress size={20} sx={{ color: '#000' }} /> : 'Sign in'}
          </Button>
          <Typography sx={{ fontSize: 11, color: '#475569', mt: 2.5, textAlign: 'center' }}>
            Access is invitation-only. Contact your administrator for an account.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
